import { useCallback, useEffect, useState, type JSX } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Editor as TiptapEditor } from '@tiptap/react';
import { extractPlaceholderNames, type FieldOptions, type TemplateDocument } from '@template-filler/core';
import { Editor } from '../editor/Editor.js';
import { Toolbar } from '../editor/Toolbar.js';
import { PlaceholderDialog } from '../editor/PlaceholderDialog.js';
import { FieldOptionsEditor } from '../editor/FieldOptionsEditor.js';
import { fieldOptionsForSave, renameFieldOptions } from '../editor/field-options-state.js';
import { toEditorContent } from '../editor/adapter.js';
import { api } from '../api/client.js';
import { describeError, isSessionExpired, isVersionConflict } from '../lib/describe-error.js';
import { signIn } from '../auth/cognito.js';

const EMPTY_DOC: TemplateDocument = { type: 'doc', content: [{ type: 'paragraph' }] };

type DialogState = { mode: 'insert' } | { mode: 'rename'; name: string } | undefined;

export function TemplateEditor(): JSX.Element {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();

  const [editor, setEditor] = useState<TiptapEditor | undefined>();
  const [title, setTitle] = useState('');
  const [doc, setDoc] = useState<TemplateDocument>(EMPTY_DOC);
  // Feature 004: allowed values per field, keyed by field name. Pruned to the
  // document's fields on save; remapped when a field is renamed.
  const [fieldOptions, setFieldOptions] = useState<FieldOptions>({});
  const [version, setVersion] = useState<number | undefined>();
  const [loading, setLoading] = useState(templateId !== undefined);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [sessionExpired, setSessionExpired] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [dialog, setDialog] = useState<DialogState>();

  useEffect(() => {
    if (!templateId) return;
    let cancelled = false;

    api
      .getTemplate(templateId)
      .then((template) => {
        if (cancelled) return;
        setTitle(template.title);
        setDoc(template.body);
        setFieldOptions(template.fieldOptions ?? {});
        setVersion(template.version);
        editor?.commands.setContent(toEditorContent(template.body));
      })
      .catch((caught: unknown) => {
        if (!cancelled) setError(describeError(caught));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [templateId, editor]);

  // Unsaved work must not vanish on a stray navigation.
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const handleChange = useCallback((next: TemplateDocument) => {
    setDoc(next);
    setDirty(true);
  }, []);

  async function save(): Promise<void> {
    setError(undefined);

    const trimmed = title.trim();
    if (trimmed.length === 0) {
      setError('Give the template a title before saving.');
      return;
    }

    // Send only options for fields the document still contains, with empty sets
    // dropped — the server reconciles too, but this keeps the payload honest.
    const options = fieldOptionsForSave(fieldOptions, extractPlaceholderNames(doc));

    setSaving(true);
    try {
      if (templateId && version !== undefined) {
        const updated = await api.updateTemplate(templateId, {
          title: trimmed,
          body: doc,
          version,
          fieldOptions: options,
        });
        setVersion(updated.version);
      } else {
        const created = await api.createTemplate({ title: trimmed, body: doc, fieldOptions: options });
        navigate(`/templates/${created.templateId}`, { replace: true });
      }
      setDirty(false);
    } catch (caught) {
      // Session expiry mid-compose. The draft stays in React state and the page
      // is not navigated away from, so re-authenticating returns the user to
      // their work rather than discarding it (spec Edge Cases, FR-004).
      if (isSessionExpired(caught)) {
        setSessionExpired(true);
      } else if (isVersionConflict(caught)) {
        setError(
          'Someone saved changes to this template in another tab or window. Reload to see their version — your unsaved changes here will be lost, so copy anything you need first.',
        );
      } else {
        setError(describeError(caught));
      }
    } finally {
      setSaving(false);
    }
  }

  const placeholderNames = extractPlaceholderNames(doc);

  if (loading) return <p>Loading…</p>;

  return (
    <main className="page page--wide">
      <header className="page-header">
        <h1>{templateId ? 'Edit template' : 'New template'}</h1>
        <button type="button" className="btn-ghost" onClick={() => navigate('/')}>
          Back to library
        </button>
      </header>

      {sessionExpired ? (
        <div className="banner banner-warning" role="alert">
          <p>
            <strong>Your session ended before this could be saved.</strong> Your work is still here
            on this page. Sign in again, then press Save — nothing has been lost.
          </p>
          <button type="button" className="btn-primary" onClick={() => void signIn()}>
            Sign in again
          </button>
        </div>
      ) : null}

      {error ? (
        <p className="error" role="alert">
          {error}
        </p>
      ) : null}

      <label htmlFor="template-title">Title</label>
      <input
        id="template-title"
        value={title}
        maxLength={200}
        onChange={(event) => {
          setTitle(event.target.value);
          setDirty(true);
        }}
      />

      <Toolbar editor={editor} onInsertField={() => setDialog({ mode: 'insert' })} />

      <Editor content={doc} onChange={handleChange} onReady={setEditor} />

      <section aria-labelledby="fields-heading" className="fields">
        <h2 id="fields-heading">Fields in this template</h2>
        {placeholderNames.length === 0 ? (
          <p className="hint">
            No fields yet. Use <strong>Insert field</strong> to mark the parts that change each time
            this template is used.
          </p>
        ) : (
          <ul>
            {placeholderNames.map((name) => (
              <li key={name}>
                <div className="field-row">
                  <span>{name}</span>
                  <button type="button" onClick={() => setDialog({ mode: 'rename', name })}>
                    Rename
                  </button>
                </div>
                <FieldOptionsEditor
                  name={name}
                  values={fieldOptions[name] ?? []}
                  onChange={(values) => {
                    setFieldOptions((prev) => {
                      const next = { ...prev };
                      if (values.length === 0) delete next[name];
                      else next[name] = values;
                      return next;
                    });
                    setDirty(true);
                  }}
                />
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="page-actions">
        <button type="button" className="btn-primary" onClick={() => void save()} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        {templateId ? (
          <button type="button" onClick={() => navigate(`/templates/${templateId}/fill`)}>
            Fill this template
          </button>
        ) : null}
      </div>

      {dialog ? (
        <PlaceholderDialog
          mode={dialog.mode}
          existingNames={placeholderNames}
          {...(dialog.mode === 'rename' ? { initialName: dialog.name } : {})}
          onCancel={() => setDialog(undefined)}
          onConfirm={(name) => {
            if (dialog.mode === 'insert') {
              editor?.chain().focus().insertPlaceholder(name).run();
            } else {
              editor?.chain().focus().renamePlaceholderEverywhere(dialog.name, name).run();
              // Move the field's allowed values to its new name (FR-011).
              setFieldOptions((prev) => renameFieldOptions(prev, dialog.name, name));
            }
            setDialog(undefined);
          }}
        />
      ) : null}
    </main>
  );
}
